import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Clock } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { toast } from '@/lib/toast'
import { systemSettingsApi } from '@/api/endpoints'
import { SectionSkeleton, SectionError, SaveStatus } from './SectionState'
import { alertLinkExpiryQuery } from './prefetch'

// ============================================================================
// Alert Link Expiry Section
// ============================================================================

const EXPIRY_OPTIONS = [
  { key: 'alertExpiry.24h', value: 24 },
  { key: 'alertExpiry.48h', value: 48 },
  { key: 'alertExpiry.7d', value: 168 },
  { key: 'alertExpiry.30d', value: 720 },
  { key: 'alertExpiry.1y', value: 8760 },
  { key: 'alertExpiry.never' as const, value: null as number | null },
] as const

export function AlertLinkExpirySection() {
  const { t } = useTranslation('settings')
  const queryClient = useQueryClient()

  const { data, isPending, isError, refetch } = useQuery(alertLinkExpiryQuery)

  const mutation = useMutation({
    mutationFn: (expiry_hours: number | null) =>
      systemSettingsApi.setAlertLinkExpiry(expiry_hours),
    onSuccess: (updated) => {
      queryClient.setQueryData(alertLinkExpiryQuery.queryKey, updated)
      const opt = EXPIRY_OPTIONS.find(o => o.value === updated.expiry_hours)
      const label = opt ? t(opt.key) : t('alertExpiry.title')
      toast.success(t('alertExpiry.setTo', { label }))
    },
    onError: () => toast.error(t('alertExpiry.updateFailed')),
  })

  // data undefined = not loaded yet (show default); data.expiry_hours null = never expires
  const currentValue = data === undefined ? 168 : data.expiry_hours

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-base font-semibold">{t('alertExpiry.title')}</h2>
        <p className="text-xs text-muted-foreground">
          {t('alertExpiry.description')}
        </p>
      </div>

      {isPending ? (
        <SectionSkeleton rows={1} />
      ) : isError && !data ? (
        <SectionError onRetry={() => refetch()} />
      ) : (
        <div className="rounded-lg border bg-card p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400">
              <Clock className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{t('alertExpiry.cardTitle')}</p>
              <p className="text-xs text-muted-foreground">
                {t('alertExpiry.cardHint')}
              </p>
              <SaveStatus pending={mutation.isPending} saved={mutation.isSuccess} />
            </div>
            <Select
              value={currentValue === null ? 'null' : String(currentValue)}
              disabled={mutation.isPending}
              onValueChange={raw => mutation.mutate(raw === 'null' ? null : Number(raw))}
            >
              <SelectTrigger className="w-44 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EXPIRY_OPTIONS.map(opt => (
                  <SelectItem key={String(opt.value)} value={opt.value === null ? 'null' : String(opt.value)}>
                    {t(opt.key)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </section>
  )
}
